import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import gsap from "gsap";
import { SITE, serviceLabelFromQuery } from "../data/site";
import Seo, { localBusinessJsonLd } from "../Components/Seo";
import ContactForm from "../Components/ContactForm";
import Picture from "../Components/Picture";
import Reveal from "../Components/Reveal";

const CHANNELS = [
  {
    kicker: "Sofort",
    title: "Anrufen",
    text: SITE.phoneDisplay,
    href: `tel:${SITE.phoneTel}`,
    note: "Kurz schildern – wir sagen, was als Nächstes geht.",
    primary: true,
  },
  {
    kicker: "Schnell",
    title: "WhatsApp",
    text: "Nachricht senden",
    href: SITE.whatsappHref,
    note: "Fotos vom Objekt helfen beim ersten Vorschlag.",
    external: true,
  },
  {
    kicker: "Schriftlich",
    title: "E-Mail",
    text: SITE.email,
    href: `mailto:${SITE.email}`,
    note: "Für Unterlagen, Pläne oder längere Beschreibungen.",
  },
];

const NEXT_STEPS = [
  {
    n: "01",
    title: "Sie schildern das Vorhaben",
    text: "Ort, Umfang, Wunschtermin – ein paar Sätze reichen.",
  },
  {
    n: "02",
    title: "Wir melden uns konkret",
    text: "Mit Rückfragen, Termin oder einem klaren Festpreis.",
  },
  {
    n: "03",
    title: "Ein Ansprechpartner bleibt",
    text: "Vom ersten Durchbruch bis zur besenreinen Übergabe.",
  },
];

export default function Contact() {
  const [params] = useSearchParams();
  const defaultService = serviceLabelFromQuery(params.get("leistung"));
  const heroRef = useRef(null);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const ctx = gsap.context(() => {
      gsap.from(".hero-anim", {
        y: 36,
        opacity: 0,
        duration: 1.05,
        stagger: 0.1,
        ease: "power3.out",
        delay: 0.08,
      });
    }, heroRef);
    return () => ctx.revert();
  }, []);

  return (
    <>
      <Seo
        title="Kontakt & Anfahrt in Mülheim an der Ruhr"
        description="CCS Gebäudemanagement kontaktieren: Telefon, E-Mail und Anfahrt in Mülheim an der Ruhr. Angebot für Abbruch, Umzug, Renovierung oder Reinigung anfragen."
        path="/kontakt"
        jsonLd={localBusinessJsonLd()}
      />

      <section className="hero is-page contact-hero" ref={heroRef}>
        <div className="hero-media">
          <div className="hero-slide is-on">
            <Picture
              src="/images/opt/start-image-1.webp"
              fallback="/images/start-image-1.jpg"
              alt="Fachkraft beim Innenabbruch mit Schutzausrüstung"
              width={976}
              height={940}
              loading="eager"
              fetchPriority="high"
            />
          </div>
        </div>
        <div className="hero-content">
          <p className="kicker hero-anim" style={{ color: "var(--cyan)" }}>
            Kontakt · Mülheim an der Ruhr
          </p>
          <h1 className="display hero-anim">
            Sagen Sie uns,
            <br />
            <em>welcher Raum</em> als Nächstes kommt.
          </h1>
          <p className="hero-lead hero-anim">
            Anruf, WhatsApp oder Formular – wir antworten mit einem klaren
            nächsten Schritt, keinem Standardtext.
          </p>
          <div className="hero-actions hero-anim">
            <a className="btn btn-primary" href="#anfrage">
              Anfrage schreiben
            </a>
            <a className="btn btn-ghost" href={`tel:${SITE.phoneTel}`}>
              {SITE.phoneDisplay}
            </a>
          </div>
          <div className="hero-meta hero-anim">
            <div>
              <strong>Antwort</strong>
              in der Regel am selben Tag
            </div>
            <div>
              <strong>Einsatzgebiet</strong>
              Mülheim, Essen, Ruhrgebiet
            </div>
            <div>
              <strong>Angebot</strong>
              Festpreis, ohne Nachträge
            </div>
          </div>
        </div>
        <p className="hero-caption">Geschäftsführung · Kölner Straße 383C</p>
      </section>

      <section className="contact-channels" aria-label="Direktkontakt">
        <div className="wrap contact-channels-grid">
          {CHANNELS.map((item) => (
            <a
              key={item.title}
              className={`contact-channel${item.primary ? " is-primary" : ""}`}
              href={item.href}
              {...(item.external
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {})}
            >
              <p className="kicker">{item.kicker}</p>
              <h2 className="display">{item.title}</h2>
              <p className="contact-channel-link">{item.text}</p>
              <p className="muted">{item.note}</p>
            </a>
          ))}
        </div>
      </section>

      <section className="section contact-main" id="anfrage">
        <div className="wrap contact-layout">
          <Reveal>
            <p className="kicker">Anfrage</p>
            <h2 className="display contact-form-title">
              Kurz beschreiben, was ansteht.
            </h2>
            <p className="muted contact-form-lead">
              Leistung, Ort und Wunschtermin reichen für eine erste Einschätzung.
              Fotos können Sie später per WhatsApp nachreichen.
            </p>
            <ContactForm key={defaultService} defaultService={defaultService} />
          </Reveal>
          <Reveal className="contact-aside" delay={80}>
            <ol className="contact-steps">
              {NEXT_STEPS.map((step) => (
                <li key={step.n}>
                  <span>{step.n}</span>
                  <div>
                    <strong>{step.title}</strong>
                    <p>{step.text}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="contact-places">
              {SITE.addresses.map((a) => (
                <div className="contact-place" key={a.label}>
                  <p className="kicker">{a.label}</p>
                  <p>
                    {a.street}
                    <br />
                    {a.zip} {a.city}
                  </p>
                </div>
              ))}
            </div>
            <div>
              <p className="kicker">Einsatzgebiet</p>
              <div className="area-list">
                {SITE.area.map((city) => (
                  <span className="chip" key={city}>
                    {city}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="contact-map-section">
        <div className="wrap contact-map-head">
          <p className="kicker" style={{ color: "var(--cyan)" }}>
            Anfahrt
          </p>
          <h2 className="display">Vor Ort in Mülheim.</h2>
          <p>
            Geschäftsführung an der Kölner Straße, Geschäftsstelle in der
            Althofstraße – beides zentral im Ruhrgebiet.
          </p>
        </div>
        <div className="wrap contact-map-frame">
          <div className="map-wrap contact-map" data-lenis-prevent>
            <iframe
              title="CCS Gebäudemanagement auf Google Maps"
              src={SITE.mapsEmbed}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <a
            className="btn btn-primary contact-map-cta"
            href={SITE.mapsDirections}
            target="_blank"
            rel="noopener noreferrer"
          >
            Route planen
          </a>
        </div>
      </section>
    </>
  );
}
